import React from "react";
import type { IPagination2 } from "../../../interfaces/Components/ITable";
import { DropDownOptionBase } from "../../staff/DropDownMultiple";

/** Stable `DropDownOptionBase.id` for custom filters shown in Selected Filters (single chip per filter). */
export const CUSTOM_FILTER_CHIP_ID = "__custom_filter_chip";

export interface IHeaderCellProps {
  row: any;
  rowIndex: number;
  value: any;
  pagination?: IPagination2;
}

export interface IRowSelectionConfig {
  selectedIds: ReadonlySet<string>;
  onToggleRow: (id: string, selected: boolean) => void;
  onTogglePage: (pageRowIds: string[], selectAll: boolean) => void;
  getRowId?: (row: any) => string;
  /** When set, rows that return false cannot be selected (checkbox disabled; excluded from page select-all). */
  isRowSelectable?: (row: any) => boolean;
  headerLabel?: string;
}

export interface IHeader {
  id: string;
  header: string | React.ReactNode;
  width?: number;
  enableSorting?: boolean;
  columnOrderFixed?: boolean;
  defaultVisibility?: boolean;
  headerContent?: React.ReactNode;
  /** Parent header label. Contiguous columns sharing the same group key render under one grouped header. */
  headerGroup?: string | React.ReactNode;
  /** Full label shown on hover when `headerGroup` is abbreviated. Set on any column in the group. */
  headerGroupTooltip?: string;
  /** Stable key for grouping (required when `headerGroup` is not a string). */
  headerGroupId?: string;
  /** Full label shown on hover when `header` is abbreviated. */
  headerTooltip?: string;
  cell?: (props: IHeaderCellProps) => React.ReactNode;
}

export interface CustomFilterQueryClause {
  value: string | string[];
  comparisonType?: string;
  arraySearchField?: string;
}

/** Single API field clause or a map of field → clause(s) for multi-field custom filters. */
export type CustomFilterApplyPayload =
  | CustomFilterQueryClause
  | Record<string, CustomFilterQueryClause | CustomFilterQueryClause[]>
  | null;

export interface PanelFilterResetInfo {
  name: string;
  defaultValue?: unknown;
  fieldName?: string;
}

export interface CustomFilterSlotProps {
  value: unknown;
  apply: (nextUiState: unknown, clause: CustomFilterApplyPayload) => void;
}

export interface IFilterData {
  name: string;
  fieldName?: string;
  type?: "text" | "date" | "select" | "multiselect" | "radio" | "hidden" | "custom";
  render?: (props: CustomFilterSlotProps) => React.ReactNode;
  defaultCustomFilterValue?: unknown;
  getCustomSelectedChips?: (filterValue: unknown) => DropDownOptionBase[];
  /** Server query clause to restore when the filter is cleared or reset to its default. */
  getDefaultQueryClause?: () => CustomFilterQueryClause | null;
  /**
   * When a custom filter writes several API fields (e.g. audit log `pk` + `timestamp`).
   * Used to clear/restore all related keys in `valueForQuery`.
   */
  customQueryFieldNames?: string[];
  getDefaultQueryClauses?: () => Record<
    string,
    CustomFilterQueryClause | CustomFilterQueryClause[]
  > | null;
  /** Optional grid width in the filters panel (12-col layout). */
  panelColSpan?: "default" | "wide" | "full";
  /**
   * Consecutive filters with the same label render together under that heading.
   * Other filters stay on their own.
   */
  panelGroup?: string;
  objectData?: DropDownOptionBase[];
  disabled?: boolean;
  hidden?: boolean;
  /**
   * Label for the synthetic "select all" option shown when `type === "radio"`.
   * Defaults to `All ${customTitle || name}`.
   */
  allOptionLabel?: string;
  comparison?:
    | "CONTAINS"
    | "BY_NAME"
    | "BY_NAME_CONTAINS"
    | "BY_ID"
    | "EQUAL"
    | "ARRAY_SEARCH_ID"
    | "ARRAY_CONTAINS"
    | "BOOLEAN"
    | "BETWEEN"
    | "IN"
    | "BETWEEN_TS"
    | "BETWEEN_TS_PRESET"
    | "NOT_EQUAL_OR_NULL"
    | "NOT_IN"
    | "SPONSOR_NAME_TOKENS"
    | "IS_DEFINED";
  getPresetRange?: (optionId: string) => string;
  disabledWrite?: boolean;
  customTitle?: string;
  disableSort?: boolean;
  sortByName?: boolean;
}

/** Filters panel layout variants for specialized screens (e.g. firm logs modal). */
export type FiltersPanelLayout = "default" | "firmLogs";

/** API filter clause applied while a quick-filter chip is active. */
export type QuickFilterQueryClause = {
  value?: unknown;
  comparisonType?: string;
  [key: string]: unknown;
};

export interface IQuickFilter {
  key: string;
  label: string;
  /** Panel values applied with the chip (shown in Selected Filters). Key = `IFilterData.name`. */
  panelValues?: Record<string, DropDownOptionBase[]>;
  /** Extra API clauses while the chip is active. Not shown in Selected Filters. */
  hiddenQuery?: Record<string, QuickFilterQueryClause>;
}

export type QuickFilterTotals = Record<string, number>;

/** Context passed when `bannerContent` is a render function on `FilteredTableCore`. */
export interface TableBannerRenderContext {
  quickFilterTotals: QuickFilterTotals;
}

export type TableBannerContent =
  | React.ReactNode
  | ((context: TableBannerRenderContext) => React.ReactNode);

export interface ISelectedFilterGroup {
  name: string;
  key: string;
  values: DropDownOptionBase[];
}

/**
 * Configuration for grouping table rows under a subtitle row. At most one grouping is
 * supported per table (single `groupBy` prop on `FilteredTableCore`).
 */
export interface IGroupByConfig {
  /** Row field whose distinct values become group subtitles. Only this field's values create groups. */
  field: string;
  /**
   * Optional additional row fields checked as an OR condition: a row is also included in a
   * group when one of these fields' values matches (or, for arrays, contains) the group's key,
   * even if the row's own `field` value differs. This allows a row to appear under more than
   * one subtitle.
   */
  additionalFields?: string[];
  /** Optional custom formatter for the subtitle text (defaults to the raw group key). */
  getGroupLabel?: (groupKey: string, sampleRow: any) => React.ReactNode;
  /** Optional group ordering (default: locale-aware ascending by group key). */
  sortGroups?: (a: string, b: string) => number;
  /** Label used for the bucket of rows whose `field` value is empty/missing (default: "Unassigned"). */
  emptyGroupLabel?: string;
  /**
   * Name of a panel filter (`IFilterData.name`) that targets the same dimension as this
   * grouping (e.g. an "Office" filter alongside grouping by office). When that filter has a
   * selected value, the grouped display collapses to a single group — labeled with the
   * selected filter option — containing every currently loaded row, instead of re-bucketing
   * each row by its own `field`/`additionalFields` value. This avoids scattering rows across
   * multiple small groups when the active filter already narrowed the data down to one
   * intended group (e.g. rows matched via an OR/`additionalFields` condition whose own primary
   * `field` differs from the filtered value). Filters unrelated to this grouping, or no
   * selection, behave normally (full multi-group bucketing).
   */
  filterName?: string;
}
